
import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, WandSparkles, FileOutput, Download, Trash2, Calendar, CirclePlus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

type Drawing = Tables<"whiteboard_history"> & {
  drawing_analyses?: {
    id: string;
    procedure_type: string;
    analysis_text: string;
    created_at: string;
    drawing_id: string;
    key_points: Json | null;
    status: "pending" | "completed" | "failed";
    updated_at: string;
    user_id: string;
  }[];
};

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

interface DrawingsListProps {
  drawings: Drawing[];
  loadedImages: Set<string>;
  loadingDrawingId: string | null;
  hasMore: boolean;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  onLoadMore: () => void;
}

export function DrawingsList({
  drawings,
  loadedImages,
  loadingDrawingId,
  hasMore,
  onLoad,
  onDelete,
  onLoadMore
}: DrawingsListProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {drawings.map((drawing) => (
          <div
            key={drawing.id}
            className="group relative flex items-center gap-4 rounded-xl border border-border/40 bg-gradient-to-r from-background to-muted/20 p-3 shadow-sm hover:shadow-md transition-all duration-200"
          >
            {/* Thumbnail */}
            <div className="relative h-20 w-24 flex-shrink-0 overflow-hidden rounded-lg border border-border/40 bg-white/80">
              <div className="absolute inset-0 z-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:12px_12px] opacity-40"></div>
              {drawing.preview_image && (
                <img
                  src={drawing.preview_image}
                  alt={drawing.title}
                  className="h-full w-full object-contain z-10 relative"
                />
              )}
              
              {/* Analysis Badge */}
              {drawing.drawing_analyses && drawing.drawing_analyses.length > 0 && (
                <div className="absolute top-1 left-1 z-20">
                  <Badge variant="secondary" className="bg-blue-500/20 border-blue-400/30 text-blue-600 text-[8px] py-0 px-1">
                    <WandSparkles className="h-2.5 w-2.5 mr-0.5" />
                    AI
                  </Badge>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-sm line-clamp-1 text-foreground/90 group-hover:text-foreground transition-colors">
                    {drawing.title}
                  </h3>
                  <div className="text-[10px] text-muted-foreground/70 whitespace-nowrap group-hover:text-muted-foreground/90 transition-colors flex items-center gap-1">
                    <Calendar className="h-3 w-3 inline-block" />
                    {format(new Date(drawing.created_at), "MMM d, yyyy")}
                  </div>
                </div>
                
                {drawing.description && (
                  <p className="text-xs text-muted-foreground/80 line-clamp-1 group-hover:text-foreground/80 transition-colors">
                    {drawing.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {drawing.tags?.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className={cn(
                        "text-[9px] px-1.5 py-0 rounded-md bg-muted/70",
                        "group-hover:bg-muted text-muted-foreground/90", 
                        "group-hover:text-foreground/90 transition-colors"
                      )}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
              <div className="flex items-center gap-1.5">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onLoad(drawing.id)}
                  disabled={loadingDrawingId === drawing.id}
                  className="h-8 px-3 text-xs bg-white/80 hover:bg-white shadow-sm"
                >
                  {loadingDrawingId === drawing.id ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <FileOutput className="h-3.5 w-3.5 mr-1 text-primary" />
                  )}
                  <span>Load</span>
                </Button>
                
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 w-8 p-0 bg-white/80 hover:bg-white shadow-sm"
                >
                  <Download className="h-3.5 w-3.5 text-primary" />
                </Button>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3 text-xs hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white/95 backdrop-blur-sm border-border/50">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your drawing
                      and remove it from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(drawing.id)}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="pt-2">
          <Button 
            variant="outline" 
            onClick={onLoadMore}
            className="w-full border-dashed border-muted-foreground/30 bg-background/70 hover:bg-background/90 text-muted-foreground shadow-sm transition-all duration-200 group"
          >
            <CirclePlus className="h-4 w-4 mr-2 text-muted-foreground/70 group-hover:text-primary/80 transition-colors" />
            <span className="group-hover:text-foreground transition-colors">Load more drawings</span>
          </Button>
        </div>
      )}
    </div>
  );
}
